import ContactLink from "@/components/ContactLink";
import { Head } from "@inertiajs/react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SectionHeading from "@/components/marketing/SectionHeading";

export default function Privacy() {
  return (
    <MarketingLayout>
      <Head title="Privacy Policy" />

      <section className="py-10 md:py-14">
        <div className="container">
          <div className="prose max-w-3xl mx-auto text-foreground">
            <SectionHeading
              as="h1"
              flourish="legal fine print"
              title="Privacy Policy"
              align="left"
              className="mb-4"
            />
            <p className="text-sm text-muted-foreground mb-10">
              Last updated: September 30, 2026
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Overview
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge ("we," "our," or "us") helps Canadian schools run
              read-a-thon fundraising campaigns. This Privacy Policy explains
              what information we collect, how we use and share it, and what
              choices you have.
            </p>
            <p className="mb-4 leading-relaxed">
              <strong>
                Important context: the relationship between you, PagePledge,
                and your child's school.
              </strong>
            </p>
            <p className="mb-4 leading-relaxed">
              When a school signs up for PagePledge, the school is the{" "}
              <strong>data controller</strong>. They decide what student
              information to upload and how to use the platform. PagePledge is
              the <strong>data processor</strong>. We provide the software and
              store the data on the school's behalf. This means:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Questions about <em>why</em> a student is on PagePledge should
                go to the school.
              </li>
              <li>
                Questions about <em>how</em> PagePledge stores or handles data
                go to us.
              </li>
              <li>
                To correct or delete a student's personal information, contact
                the school. They can make changes directly, or contact us if
                they need help.
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              The school is responsible for providing all required notices,
              obtaining all necessary consents, and otherwise complying with
              applicable privacy laws with respect to the collection,
              transfer, and use of the information by us in order to provide
              our services to them. We will only use the information received
              from the school for the specific reason for which it was
              provided to us and in accordance with this Privacy Policy and
              our service agreement with the school.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              What we collect
            </h2>
            <p className="mb-4 leading-relaxed">
              <strong>From schools and school staff:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>School name, address, and contact information</li>
              <li>
                Names, emails, and roles of school administrators and teachers
                who use PagePledge
              </li>
              <li>Campaign settings (goals, dates, descriptions)</li>
              <li>
                Student roster data that schools upload: first name, last name,
                classroom, and grade. Full student last names are shortened before
                storage; only the abbreviation and a keyed fingerprint for matching
                repeat uploads are retained.
              </li>
              <li>
                Technical usage data (sign-in events, IP addresses, browser
                info) for security and troubleshooting
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              <strong>From parents who activate their child's profile:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Email address</li>
              <li>
                Which student(s) you have activated. Your email address is
                visible to administrators at your child's school, so they can
                see who follows each student and remove access that shouldn't
                be there.
              </li>
              <li>Reading minutes you log for your child</li>
              <li>
                Technical usage data (sign-in events, IP addresses, browser
                info)
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              <strong>From donors:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Name and email address</li>
              <li>The amount donated and optional public message</li>
              <li>
                Payment card information is{" "}
                <strong>handled entirely by Square</strong>; PagePledge never
                sees card numbers or stores them
              </li>
              <li>Technical usage data</li>
            </ul>
            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              What we do NOT collect
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>We do not collect or store photos of children.</li>
              <li>
                We do not collect student birthdays, home addresses, health
                information, or academic performance.
              </li>
              <li>We do not sell data to anyone, ever.</li>
              <li>
                We do not share data with advertisers, marketers, or data
                brokers.
              </li>
              <li>
                We do not use student data to train AI or machine learning
                models.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              How we use it
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                To operate the platform: display student pages, accept
                donations, log reading minutes, generate take-home sheets, run
                leaderboards.
              </li>
              <li>
                To send transactional emails: donation receipts, magic-link
                sign-in emails, campaign status updates, daily or weekly
                digests (which you can unsubscribe from).
              </li>
              <li>
                To keep the platform secure: rate limiting, fraud prevention,
                abuse detection.
              </li>
              <li>
                To improve the product: aggregate, anonymous usage patterns
                (never individual students).
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Your consent
            </h2>
            <p className="mb-4 leading-relaxed">
              We will collect, use, or disclose your personal information only
              with your knowledge and consent, except where required or
              permitted by applicable privacy laws. When you choose to provide
              us with your personal information, you consent to the use of
              your personal information as identified in this Privacy Policy
              and as may be further identified at the time of collection.
            </p>
            <p className="mb-4 leading-relaxed">
              You may notify us at any time that you wish to withdraw or
              change your consent to our use and disclosure of your personal
              information. We will accommodate your request subject to legal
              and contractual restrictions.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Who we share data with
            </h2>
            <p className="mb-4 leading-relaxed">
              We share data only with the service providers we need to operate
              PagePledge:
            </p>
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead className="bg-secondary">
                  <tr>
                    <th className="px-4 py-2 text-left">Provider</th>
                    <th className="px-4 py-2 text-left">What they see</th>
                    <th className="px-4 py-2 text-left">Why</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>Square</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Donation amounts, donor names and email addresses, the
                      card details you enter in Square's payment form, and which
                      student or school the donation supports
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Payment processing
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>Amazon Simple Email Service (AWS)</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Recipient email addresses and email contents
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Sending transactional emails
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>DigitalOcean</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      All application data (they host our servers and database in Toronto, Canada)
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Hosting and infrastructure
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>AppSignal</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Error reports, performance data, and server logs, which
                      include IP addresses and browser information. Names,
                      email addresses, messages, and sign-in or receipt links
                      are filtered out before anything is sent
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Keeping the service running and fixing problems
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>Google Fonts</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Your IP address and browser information when a page
                      loads our fonts
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Displaying the site's typefaces
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t border-border px-4 py-2">
                      <strong>Google Analytics</strong>
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      On our public website only (home, pricing, help, legal,
                      and signup pages): the pages you visit, how you arrived,
                      your approximate location, device and browser
                      information, and a cookie that recognizes repeat visits.
                      The cookie is off by default in Quebec, and anyone can
                      turn it on or off under Cookie settings in the website
                      footer. Never used on student, school, donation, or
                      signed-in pages
                    </td>
                    <td className="border-t border-border px-4 py-2">
                      Understanding how visitors find and use our public
                      website
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mb-4 leading-relaxed">
              None of them are allowed to use your data for any purpose other
              than providing services to PagePledge.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Where data is stored
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge's servers and database are hosted in{" "}
              <strong>Canada</strong> (or another region with comparable
              privacy protections, as offered by our hosting provider). We
              choose Canadian hosting whenever available.
            </p>
            <p className="mb-4 leading-relaxed">
              Some of our service providers process data outside Canada:
              Square and Google may process data in the United States, and
              AppSignal stores error reports and server logs in the European
              Union. We rely on standard contractual clauses and their own
              privacy certifications for these transfers.
            </p>
            <p className="mb-4 leading-relaxed">
              In these instances, your personal information may be accessible
              to law enforcement and regulatory authorities in accordance with
              the USA PATRIOT Act and other applicable laws of the United
              States.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              How long we keep data
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Active school data:</strong> kept as long as the
                school's account is active.
              </li>
              <li>
                <strong>Archived schools:</strong> data is kept for 2 years
                after the school is archived, then permanently deleted.
              </li>
              <li>
                <strong>Rejected or abandoned school applications:</strong>{" "}
                deleted after 180 days.
              </li>
              <li>
                <strong>Magic-link tokens:</strong> deleted within 7 days of
                use or expiry.
              </li>
              <li>
                <strong>Donor records:</strong> kept as long as the donation
                exists (needed for refund and receipt purposes).
              </li>
              <li>
                <strong>Sign-in records and payment event logs:</strong>{" "}
                deleted after 90 days.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Your rights
            </h2>
            <p className="mb-4 leading-relaxed">
              Subject to applicable privacy laws, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Access</strong> the personal information we hold about
                you
              </li>
              <li>
                <strong>Correct</strong> inaccurate information
              </li>
              <li>
                <strong>Withdraw consent</strong> at any time
              </li>
              <li>
                <strong>File a complaint</strong> with the Office of the
                Privacy Commissioner of Canada
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              <strong>To exercise any of these rights:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Student information:</strong> contact your child's
                school first. They can make most changes directly.
              </li>
              <li>
                <strong>Parent, teacher, or donor information:</strong> email
                us at{" "}
                <strong>
                  <ContactLink topic="privacy"
                    className="text-primary hover:underline"
                  >
                    privacy@pagepledge.com
                  </ContactLink>
                </strong>
                . We will respond within 30 days.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Children's privacy
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge is designed for use by schools, which have existing
              permissions to collect basic student information (first name,
              last name, classroom, grade) as part of normal school operations.
              We never collect other information about students beyond what
              the school uploads.
            </p>
            <p className="mb-4 leading-relaxed">
              We do not knowingly allow children under 13 to create their own
              PagePledge accounts. Student profiles are created by school
              staff; parents activate them using a code sent home by the school.
              Children do not sign in to PagePledge directly.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Quebec residents
            </h2>
            <p className="mb-4 leading-relaxed">
              If you live in Quebec, you have additional rights under
              provincial privacy laws:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                The right to request that your personal information be
                deleted, subject to legal and contractual restrictions.
              </li>
              <li>
                The right to be informed about automated decision-making
                (PagePledge does not make any automated decisions about you).
              </li>
              <li>The right to data portability.</li>
              <li>
                Analytics cookies on our public website are off until you
                turn them on under Cookie settings in the website footer.
              </li>
              <li>
                The right to know our Privacy Officer's contact information:{" "}
                <strong>
                  Jordan Yeo,{" "}
                  <ContactLink topic="privacy"
                    className="text-primary hover:underline"
                  >
                    privacy@pagepledge.com
                  </ContactLink>
                </strong>
                .
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Security
            </h2>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                Passwords are not used; authentication is via short-lived
                magic-link emails.
              </li>
              <li>All data is transmitted over HTTPS.</li>
              <li>
                Sensitive fields (access tokens, webhook keys) are encrypted at
                rest.
              </li>
              <li>
                We limit staff access to production data to those who need it
                for support.
              </li>
              <li>
                We log and monitor sign-in events for unusual patterns.
              </li>
            </ul>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Changes to this policy
            </h2>
            <p className="mb-4 leading-relaxed">
              We will update this page if our practices change. Material
              changes will be announced via email to school administrators.
              Changes are effective upon our updating this page and will apply
              to existing personal information held by us and to personal
              information collected thereafter. Please review this Privacy
              Policy each time you use our website to ensure you remain
              informed of our current policies and practices.
            </p>

            <h2 className="font-display font-semibold text-2xl md:text-3xl mt-10 mb-4">
              Contact
            </h2>
            <p className="mb-4 leading-relaxed">
              PagePledge
              <br />
              Email:{" "}
              <ContactLink topic="privacy"
                className="text-primary hover:underline"
              >
                privacy@pagepledge.com
              </ContactLink>
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
